<template>
  <div>
    <section class="page-hero" :style="{ '--ph-img': `url(${images.hero3})` }">
      <div class="wrap">
        <p class="eyebrow reveal">Get in touch</p>
        <h1 class="reveal">Tell us how you live, we'll craft the rest.</h1>
        <p class="reveal">Share your vision with us, and let's explore how a space can be shaped around the way you actually live.</p>
        <p class="crumb reveal"><router-link to="/">Home</router-link> · Contact</p>
      </div>
    </section>

    <section class="section">
      <div class="wrap contact-grid">
        <div class="reveal">
          <p class="eyebrow">Say hello</p>
          <h2>We'd love to hear from you.</h2>
          <p class="lead">
            Whether it's a full renovation or a single room, every project starts with a
            conversation. We work with homeowners across Singapore, so reach out and we'll be in
            touch within two working days.
          </p>

          <div class="locations">
            <div v-for="loc in locations" :key="loc.label" class="loc">
              <small>{{ loc.label }}</small>
              <p>
                <span class="loc-name">{{ loc.name }}</span><br />
                <span v-for="line in loc.lines" :key="line">{{ line }}<br /></span>
              </p>
              <a :href="loc.url || mapsUrl(loc.query)" target="_blank" rel="noopener" class="loc-map">
                View on map
                <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
                  <path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M7 17 17 7M9 7h8v8" />
                </svg>
              </a>
            </div>
            <div class="loc">
              <small>Email</small>
              <p><a :href="`mailto:${email}`">{{ email }}</a></p>
            </div>
            <div class="loc">
              <small>Instagram</small>
              <p><a :href="instagram" target="_blank" rel="noopener">{{ instagramHandle }}</a></p>
            </div>
          </div>
        </div>

        <form class="enquiry reveal" @submit.prevent="submit">
          <div v-if="sent" style="text-align: center; padding: 1rem 0">
            <div style="font-family: var(--serif); font-size: 2rem; color: var(--terracotta)">Thank you.</div>
            <p style="color: var(--muted); margin-top: 0.6rem">We've received your enquiry and will be in touch soon.</p>
            <button type="button" class="btn btn-dark" style="margin-top: 1.6rem" @click="reset">Send another</button>
          </div>

          <template v-else>
            <div class="field">
              <label for="name">Name</label>
              <input id="name" v-model="form.name" type="text" required :disabled="sending" placeholder="Your name" />
            </div>
            <div class="field">
              <label for="phone">Contact number</label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                required
                :disabled="sending"
                placeholder="9123 4567"
              />
            </div>
            <div class="field">
              <label for="type">Property type</label>
              <select id="type" v-model="form.type" :disabled="sending">
                <option v-for="t in propertyTypes" :key="t">{{ t }}</option>
              </select>
            </div>
            <div class="field">
              <label for="address">Project address</label>
              <input
                id="address"
                v-model="form.address"
                type="text"
                autocomplete="street-address"
                :disabled="sending"
                placeholder="Block, street and unit"
              />
              <p class="field-hint">Optional. Even just the development or estate helps us plan a site visit.</p>
            </div>
            <div class="field">
              <label for="keydate">Key collection date</label>
              <input id="keydate" v-model="form.keyDate" type="date" :disabled="sending" />
              <p class="field-hint">Optional. An estimate is fine, or leave blank if you've already collected.</p>
            </div>
            <div class="field">
              <label for="completion">Desired completion date</label>
              <select id="completion" v-model="form.completion" :disabled="sending">
                <option value="">Select…</option>
                <option v-for="c in completionOptions" :key="c">{{ c }}</option>
              </select>
              <p class="field-hint">Optional.</p>
            </div>
            <div class="field">
              <label for="budget">Estimated budget</label>
              <select id="budget" v-model="form.budget" :disabled="sending">
                <option value="">Select…</option>
                <option v-for="b in budgetOptions" :key="b">{{ b }}</option>
              </select>
              <p class="field-hint">Optional. A rough range helps us suggest what's realistic.</p>
            </div>
            <!-- Date and time read as one question. The time slot only appears
                 once a date is picked — a time with no date tells the team nothing. -->
            <div class="field">
              <label for="apptdate">Next available date / time</label>
              <div class="field-inline" :class="{ 'has-time': form.apptDate }">
                <input id="apptdate" v-model="form.apptDate" type="date" :min="today" :disabled="sending" />
                <select
                  v-if="form.apptDate"
                  id="appttime"
                  v-model="form.apptTime"
                  aria-label="Preferred time"
                  :disabled="sending"
                >
                  <option value="">Preferred time…</option>
                  <option v-for="t in appointmentTimes" :key="t">{{ t }}</option>
                </select>
              </div>
              <p class="field-hint">
                Optional. The earliest date you're free to meet us. By appointment only — we'll try to
                match your preferred timing and reply you to confirm.
              </p>
            </div>
            <div class="field">
              <label for="msg">Before we start</label>
              <textarea id="msg" v-model="form.message" rows="3" :disabled="sending" placeholder="Anything you will like to tell us..."></textarea>
              <p class="field-hint">
                Have a floor plan? WhatsApp it to us at
                <a :href="whatsappLink" target="_blank" rel="noopener">{{ whatsappDisplay }}</a>.
              </p>
            </div>

            <div class="field-check">
              <input id="wagroup" v-model="form.whatsappGroup" type="checkbox" :disabled="sending" />
              <label for="wagroup">
                Add me to a WhatsApp group with the EIKA team.
                <span class="field-hint">
                  We'll use the contact number above. It keeps designer, project manager and you in
                  one thread — no chasing over email.
                </span>
              </label>
            </div>

            <!-- Honeypot: invisible to people, irresistible to bots -->
            <input
              v-model="form.botcheck"
              type="text"
              name="botcheck"
              class="hp-field"
              tabindex="-1"
              autocomplete="off"
              aria-hidden="true"
            />

            <div ref="captcha" class="captcha-box"></div>

            <p v-if="error" class="form-error">{{ error }}</p>

            <button type="submit" class="btn btn-primary" :disabled="sending">
              {{ sending ? "Sending…" : "Send enquiry" }}
            </button>
          </template>
        </form>
      </div>
    </section>

    <GoogleReviews :tint="true" />
  </div>
</template>

<script>
import GoogleReviews from "@/components/GoogleReviews.vue";
import { images } from "@/data/projects";
import {
  locations,
  email,
  instagram,
  instagramHandle,
  mapsUrl,
  web3formsKey,
  web3formsEndpoint,
  hcaptchaSitekey,
  whatsapp,
  propertyTypes,
  completionOptions,
  budgetOptions,
  appointmentTimes,
} from "@/data/contact";

const EMPTY = {
  name: "",
  phone: "",
  type: propertyTypes[0],
  address: "",
  keyDate: "",
  completion: "",
  budget: "",
  apptDate: "",
  apptTime: "",
  message: "",
  whatsappGroup: false,
  botcheck: "",
};

/** Load hCaptcha's API once, shared across mounts. */
function loadHcaptcha() {
  if (window.hcaptcha) return Promise.resolve();
  if (window.__hcaptchaPromise) return window.__hcaptchaPromise;
  window.__hcaptchaPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // hcaptcha may attach a tick after onload
      const start = Date.now();
      (function poll() {
        if (window.hcaptcha) return resolve();
        if (Date.now() - start > 5000) return reject(new Error("hcaptcha did not initialise"));
        setTimeout(poll, 50);
      })();
    };
    s.onerror = () => reject(new Error("failed to load hcaptcha"));
    document.head.appendChild(s);
  });
  return window.__hcaptchaPromise;
}

export default {
  name: "ContactView",
  components: { GoogleReviews },
  data() {
    return {
      images,
      locations,
      email,
      instagram,
      instagramHandle,
      sent: false,
      sending: false,
      error: "",
      form: { ...EMPTY },
      captchaToken: "",
      captchaReady: false,
      widgetId: null,
      propertyTypes,
      completionOptions,
      budgetOptions,
      appointmentTimes,
    };
  },
  computed: {
    // Local date, not UTC — toISOString() alone would offer "yesterday" to
    // anyone in SGT before 8am.
    today() {
      const d = new Date();
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    },
    whatsappLink() {
      return `https://wa.me/${whatsapp}?text=${encodeURIComponent(
        "Hi EIKA, here's the floor plan for my home."
      )}`;
    },
    // 6565560837 -> 6556 0837
    whatsappDisplay() {
      const local = whatsapp.replace(/^65/, "");
      return `${local.slice(0, 4)} ${local.slice(4)}`;
    },
  },
  watch: {
    // Clearing the date hides the time dropdown, so drop the value with it —
    // otherwise the enquiry mails a time slot with no date attached.
    "form.apptDate"(value) {
      if (!value) this.form.apptTime = "";
    },
  },
  mounted() {
    this.initCaptcha();
  },
  methods: {
    mapsUrl,
    async initCaptcha() {
      try {
        await loadHcaptcha();
        if (!this.$refs.captcha || this.widgetId !== null) return;
        this.widgetId = window.hcaptcha.render(this.$refs.captcha, {
          sitekey: hcaptchaSitekey,
          callback: (token) => {
            this.captchaToken = token;
            this.error = "";
          },
          // Tokens are single-use and expire — clear on both.
          "expired-callback": () => (this.captchaToken = ""),
          "error-callback": () => (this.captchaToken = ""),
        });
        this.captchaReady = true;
      } catch (e) {
        // If hCaptcha can't load, don't strand the visitor — let them submit and
        // let the server decide, rather than blocking a real enquiry.
        this.captchaReady = false;
        console.warn("[enquiry] hcaptcha unavailable:", e);
      }
    },
    resetCaptcha() {
      this.captchaToken = "";
      if (this.widgetId !== null && window.hcaptcha) {
        try {
          window.hcaptcha.reset(this.widgetId);
        } catch (e) {
          /* widget already gone */
        }
      }
    },
    reset() {
      this.sent = false;
      this.error = "";
      this.form = { ...EMPTY };
      this.widgetId = null;
      this.captchaToken = "";
      this.$nextTick(this.initCaptcha);
    },
    async submit() {
      // Bot filled the hidden field — pretend it worked, send nothing.
      if (this.form.botcheck) {
        this.sent = true;
        return;
      }
      if (!web3formsKey) {
        this.error = "The form isn't configured yet. Please email us at " + email + ".";
        return;
      }
      // Only insist on a token if the widget actually rendered.
      if (this.captchaReady && !this.captchaToken) {
        this.error = "Please complete the captcha below so we know you're human.";
        return;
      }

      this.sending = true;
      this.error = "";
      try {
        // FormData rather than JSON — it's the only way Web3Forms accepts a file.
        // Deliberately no Content-Type header: the browser must set the
        // multipart boundary itself, and setting it by hand breaks the upload.
        const fd = new FormData();
        fd.append("access_key", web3formsKey);
        fd.append("subject", `New ${this.form.type} enquiry from ${this.form.name}`);
        fd.append("from_name", "EIKA Website");
        // No replyto: the form no longer collects an email, so replies go to
        // the inbox the access key is registered to. Follow up by phone.
        fd.append("name", this.form.name);
        fd.append("contact_number", this.form.phone);
        fd.append("property_type", this.form.type);
        fd.append("project_address", this.form.address || "Not provided");
        fd.append("key_collection_date", this.form.keyDate || "Not provided");
        fd.append("desired_completion", this.form.completion || "Not provided");
        fd.append("estimated_budget", this.form.budget || "Not provided");
        fd.append("next_available_date", this.form.apptDate || "Not provided");
        fd.append("preferred_time", this.form.apptTime || "Not provided");
        fd.append("message", this.form.message || "Not provided");
        fd.append("whatsapp_group", this.form.whatsappGroup ? "YES — add to WhatsApp group" : "No");
        fd.append("h-captcha-response", this.captchaToken);

        const res = await fetch(web3formsEndpoint, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.message || `Request failed (${res.status})`);
        }
        this.sent = true;
        this.form = { ...EMPTY };
        this.widgetId = null;
        this.captchaToken = "";
      } catch (e) {
        // Never lose the lead — give them a way to reach us anyway.
        this.error = `Sorry, something went wrong sending that. Please try again, or email us directly at ${email}.`;
        console.error("[enquiry] submit failed:", e);
        // hCaptcha tokens are single-use — a retry needs a fresh one.
        this.resetCaptcha();
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
